import { useRef, useCallback, useEffect } from "react";
import api from "../services/api";
import { useVoicePreviewAudio } from "./useVoicePreviewAudio";

const GPT4O_ONLY_VOICES = new Set(["ballad", "verse", "marin", "cedar"]);

export const getEffectiveTtsModel = (voice, ttsModel) => {
  const normalizedVoice = String(voice || "").toLowerCase();
  const model = ttsModel || "tts-1";
  if (GPT4O_ONLY_VOICES.has(normalizedVoice) && (model === "tts-1" || model === "tts-1-hd")) {
    return "gpt-4o-mini-tts";
  }
  return model;
};

const parsePreviewError = async (err) => {
  if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED") {
    return null;
  }

  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const json = JSON.parse(text);
      return json?.error || json?.message || err?.message;
    } catch (_) {}
  }

  return err?.response?.data?.error || err?.message || "Falha ao gerar prévia.";
};

export const useTtsPreview = () => {
  const { playBlob, stop, release } = useVoicePreviewAudio();
  const abortRef = useRef(null);

  useEffect(() => () => {
    abortRef.current?.abort();
    release();
  }, [release]);

  const playPreview = useCallback(async (payload) => {
    if (abortRef.current) {
      abortRef.current.abort();
    }

    const controller = new AbortController();
    abortRef.current = controller;

    const body = {
      ...payload,
      ttsModel: getEffectiveTtsModel(payload.voice, payload.ttsModel)
    };

    try {
      const response = await api.post("/tts/preview", body, {
        responseType: "blob",
        signal: controller.signal
      });

      if (controller.signal.aborted) {
        return false;
      }

      await playBlob(response.data);
      return true;
    } catch (err) {
      const message = await parsePreviewError(err);
      if (message === null) {
        return false;
      }
      throw new Error(message);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
    }
  }, [playBlob]);

  return {
    playPreview,
    stopPreview: stop,
    releasePreview: release,
    getEffectiveTtsModel
  };
};
